var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { Auth } from '@freelancer/auth';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import * as Rx from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, publishReplay, refCount, startWith, switchMap, take, tap, } from 'rxjs/operators';
import { StoreBackend } from './backend';
import { DatastoreCollection } from './datastore-collection';
import { DatastoreDocument } from './datastore-document';
import { LOGGED_OUT_KEY } from './datastore.interface';
import { arrayIsShallowEqual, isDefined, objectFilterByKeys, sameElements, setDiff, toNumber, toObservable, } from './helpers';
import { DatastoreMissingModuleError } from './missing-module-error';
import { duplicateFirst } from './operators/duplicateFirst';
import { select } from './operators/select';
import { RequestDataConfig } from './request-data';
import { requestStatusesEqual, RequestStatusHandler, } from './request-status-handler.service';
import { SearchManager } from './search';
import { flattenQuery, selectEntitiesForQuery } from './store.helpers';
import { WebSocketService } from './websocket';
var Datastore = /** @class */ (function () {
    function Datastore(store$, action$, storeBackend, webSocketService, searchManager, requestStatusHandler, auth, requestDataConfig) {
        this.store$ = store$;
        this.action$ = action$;
        this.storeBackend = storeBackend;
        this.webSocketService = webSocketService;
        this.searchManager = searchManager;
        this.requestStatusHandler = requestStatusHandler;
        this.auth = auth;
        this.requestDataConfig = requestDataConfig;
    }
    /**
     * Returns a collection of objects, customised by query.
     *
     * @param collectionName Collection name
     * @param queryFn Query to refine results
     */
    Datastore.prototype.collection = function (collectionName, queryFn) {
        var _this = this;
        var flattenedQuery$ = flattenQuery(queryFn);
        var refStream$ = Rx.combineLatest([
            toObservable(collectionName),
            toObservable(this.auth.authState$),
            flattenedQuery$,
        ]).pipe(map(function (_a) {
            var collection = _a[0], authState = _a[1], _b = _a[2], limit = _b.limit, params = _b.params, order = _b.order;
            return ({
                path: {
                    collection: collection,
                    authUid: authState ? authState.userId : LOGGED_OUT_KEY,
                },
                query: { limit: limit, params: params, isDocumentQuery: false },
                order: order,
            });
        }));
        // Requests to the backend, further processed in `request-data.effect.ts`
        var requestStream$ = refStream$.pipe(map(function (ref) {
            var request = {
                type: ref.path.collection,
                ref: ref,
                clientRequestIds: [_this.generateClientRequestId()],
            };
            return request;
        }), tap(function (request) {
            // Only dispatch to store on a non-null query
            if (request.ref.query && request.ref.query.params) {
                var action = {
                    type: 'REQUEST_DATA',
                    payload: request,
                };
                _this.store$.dispatch(action);
            }
        }), publishReplay(1), refCount());
        var sourceStream$ = requestStream$.pipe(tap(function (request) {
            return _this.requestStatusHandler.update(request, {
                ready: false,
            });
        }), switchMap(function (request) {
            var ref = request.ref, _a = request.ref, _b = _a.path, collection = _b.collection, authUid = _b.authUid, query = _a.query;
            var defaultOrder = _this.storeBackend.defaultOrder(collection);
            var source$ = // grab the collection items from the store
             query && !query.params
                ? Rx.of([]) // immediately emit on null query
                : _this.store$.pipe(
                // store$ emits on every action dispatched and store state change
                tap(function (source) { return _this.verifyModuleIsImported(collection, source); }), // TODO: T75102
                select(collection, authUid), filter(isDefined), map(function (storeSlice) {
                    return selectEntitiesForQuery(storeSlice, ref, defaultOrder);
                }), filter(isDefined));
            return source$.pipe(tap(function () {
                // Update request status here on fetching from either network or cache
                return _this.requestStatusHandler.update(request, {
                    ready: true,
                });
            }), distinctUntilChanged(arrayIsShallowEqual));
        }), publishReplay(1), refCount());
        var statusStream$ = Rx.combineLatest([
            requestStream$.pipe(switchMap(function (request) { return _this.requestStatusHandler.get$(request); })),
            // combining with sourceStream to set ready flag to false on initial subscribe
            sourceStream$.pipe(startWith(undefined)),
        ]).pipe(map(function (_a) {
            var status = _a[0];
            return status;
        }), distinctUntilChanged(requestStatusesEqual), publishReplay(1), refCount());
        return new DatastoreCollection(refStream$, this.storeBackend, statusStream$, Rx.combineLatest([
            statusStream$.pipe(startWith(undefined)),
            sourceStream$,
        ]).pipe(map(function (_a) {
            var source = _a[1];
            return source;
        }), distinctUntilChanged()));
    };
    Datastore.prototype.document = function (collectionName, documentId$, documentQuery$) {
        var _this = this;
        var refStream$ = Rx.combineLatest([
            toObservable(collectionName),
            toObservable(this.auth.authState$),
            toObservable(documentId$).pipe(distinctUntilChanged()),
            toObservable(documentQuery$).pipe(distinctUntilChanged(function (a, b) {
                return !!a &&
                    !!b &&
                    a.caseInsensitive === b.caseInsensitive &&
                    a.index === b.index;
            })),
        ]).pipe(map(function (_a) {
            var _b;
            var collection = _a[0], authState = _a[1], id = _a[2], documentQuery = _a[3];
            var path = {
                collection: collection,
                authUid: authState ? authState.userId : LOGGED_OUT_KEY,
            };
            if (!id) {
                // Treat as a document query with no params
                return {
                    path: path,
                    query: {
                        isDocumentQuery: true,
                        limit: 1,
                        params: {},
                    },
                };
            }
            return documentQuery
                ? {
                    path: path,
                    query: {
                        isDocumentQuery: true,
                        limit: 1,
                        params: (_b = {},
                            _b[documentQuery.index] = [
                                {
                                    name: documentQuery.index,
                                    condition: documentQuery.caseInsensitive
                                        ? 'equalsIgnoreCase'
                                        : '==',
                                    value: id,
                                },
                            ],
                            _b),
                    },
                }
                : {
                    path: __assign({}, path, { ids: [id.toString()] }),
                };
        }));
        var requestStream$ = refStream$.pipe(map(function (ref) { return ({
            type: ref.path.collection,
            ref: ref,
            clientRequestIds: [_this.generateClientRequestId()],
        }); }), tap(function (request) {
            var action = {
                type: 'REQUEST_DATA',
                payload: request,
            };
            _this.store$.dispatch(action);
        }), publishReplay(1), refCount());
        // TODO: Rx.merge causes double emission - T53648
        var sourceStream$ = (collectionName === 'onlineOffline' // include websocket subscription stream
            ? Rx.merge(Rx.combineLatest([
                this.webSocketService.subscribeOnlineOffline(requestStream$.pipe(map(function (request) { return request.ref.path.ids; }), filter(isDefined))),
                requestStream$,
            ]).pipe(map(function (_a) {
                var ss = _a[0], req = _a[1];
                return req;
            })), requestStream$)
            : requestStream$).pipe(tap(function (request) {
            return _this.requestStatusHandler.update(request, {
                ready: false,
            });
        }), switchMap(function (request) {
            var ref = request.ref, _a = request.ref.path, collection = _a.collection, authUid = _a.authUid;
            var document$ = _this.store$.pipe(tap(function (source) { return _this.verifyModuleIsImported(collection, source); }), // TODO: T75102
            select(collection, authUid), filter(isDefined), map(function (state) { return _this.fetchSingleObject(state, ref); }));
            return Rx.combineLatest([
                document$,
                _this.action$.pipe(startWith(undefined)),
            ]).pipe(
            // Update request status here on fetching from either network or cache
            tap(function (_a) {
                var document = _a[0], action = _a[1];
                // Because the missing items check in RequestDataEffect can fail
                // when requests are batched, this is a hacky way to check that the
                // item asked for in the request does not exist even after a
                // "successful" fetch.
                // TODO: If the document is cached but then deleted this check is
                // not sufficient - need to address other actions that mutate store
                if (action &&
                    action.type === 'API_FETCH_SUCCESS' &&
                    request.clientRequestIds.every(function (id) {
                        return action.payload.clientRequestIds.some(function (actionId) { return actionId === id; });
                    }) &&
                    document === undefined) {
                    // TODO: replace with ErrorHandler
                    console.warn("Fetch succeeded but object(s) not found from document call to '" + request.type + "'. Result was", action.payload.result);
                    _this.requestStatusHandler.update(request, {
                        ready: false,
                        error: {
                            errorCode: ErrorCodeApi.NOT_FOUND,
                        },
                    });
                }
            }), map(function (_a) {
                var document = _a[0];
                return document;
            }), filter(isDefined), tap(function () {
                _this.requestStatusHandler.update(request, {
                    ready: true,
                });
            }), distinctUntilChanged());
        }), publishReplay(1), refCount());
        var statusStream$ = Rx.combineLatest([
            requestStream$.pipe(switchMap(function (request) { return _this.requestStatusHandler.get$(request); })),
            // combining with sourceStream to set ready flag to false on initial subscribe
            sourceStream$.pipe(startWith(undefined)),
        ]).pipe(map(function (_a) {
            var status = _a[0];
            return status;
        }), distinctUntilChanged(requestStatusesEqual), publishReplay(1), refCount());
        return new DatastoreDocument(refStream$, this.storeBackend, statusStream$, Rx.combineLatest([
            statusStream$.pipe(startWith(undefined)),
            sourceStream$,
        ]).pipe(map(function (_a) {
            var source = _a[1];
            return source;
        }), distinctUntilChanged()));
    };
    Datastore.prototype.fetchSingleObject = function (storeSlice, ref) {
        var _a = ref.path, collection = _a.collection, ids = _a.ids;
        if (ids) {
            return storeSlice.entities[ids[0]] && storeSlice.entities[ids[0]].entity;
        }
        var defaultOrder = this.storeBackend.defaultOrder(collection);
        var entities = selectEntitiesForQuery(storeSlice, ref, defaultOrder);
        return entities ? entities[0] : undefined;
    };
    /**
     * Returns a list of objects from a collection specified by id.
     * If an observable is passed in for `objectIds` then only the delta will be
     * refetched from the network.
     *
     * @param collectionName Collection name
     * @param objectIds$
     */
    Datastore.prototype.objects = function (collectionName, objectIds$) {
        var _this = this;
        var requestStream$ = Rx.combineLatest([
            toObservable(collectionName),
            toObservable(this.auth.authState$),
            // FIXME: this should be ReadonlyArray<number> | ReadonlyArray<string>
            // https://github.com/Microsoft/TypeScript/issues/7294
            toObservable(objectIds$).pipe(distinctUntilChanged(arrayIsShallowEqual)),
        ]).pipe(map(function (_a) {
            var collection = _a[0], authState = _a[1], ids = _a[2];
            var path = {
                collection: collection,
                authUid: authState ? authState.userId : LOGGED_OUT_KEY,
                ids: ids.map(function (id) { return id.toString(); }),
            };
            return { path: path };
        }), 
        // Add an path at the start with an empty list of ids
        duplicateFirst(function (_a) {
            var _b = _a.path, collection = _b.collection, authUid = _b.authUid;
            return ({
                path: { collection: collection, authUid: authUid, ids: [] },
            });
        }), pairwise(), // Use pairwise to calculate and fetch new objects only
        switchMap(function (paths) {
            var _a = paths[0].path, collection = _a.collection, authUid = _a.authUid;
            return _this.store$.pipe(select(collection, authUid), take(1), map(function (data) { return ({ paths: paths, data: data }); }));
        }), map(function (_a) {
            var _b = _a.paths, idsOrig = _b[0].path.ids, _c = _b[1].path, collection = _c.collection, authUid = _c.authUid, idsNew = _c.ids, data = _a.data;
            // Do not refetch entities that have been fetched recently. Useful for
            // projections where multiple collections are populated with one
            // datastore call, e.g. `threads_get` with a `user_details` projection.
            // While projections exist this must co-exist with the dedupe in
            // RequestDataEffect, which is collection-specific
            var newIds = setDiff(idsNew, idsOrig).filter(function (id) {
                return data &&
                    data.entities[id] &&
                    _this.requestDataConfig.dedupeWindowTime > 0
                    ? toNumber(data.entities[id].timeFetched) +
                        _this.requestDataConfig.dedupeWindowTime <
                        Date.now()
                    : true;
            });
            var action = {
                type: 'REQUEST_DATA',
                payload: {
                    type: collection,
                    ref: { path: { collection: collection, authUid: authUid, ids: newIds } },
                    clientRequestIds: [_this.generateClientRequestId()],
                },
            };
            if (newIds.length) {
                _this.store$.dispatch(action);
            }
            return __assign({}, action.payload, { ref: { path: { collection: collection, authUid: authUid, ids: idsNew } } });
        }), publishReplay(1), refCount());
        // FIXME: This will double emit for `collection === 'onlineOffline'` due to Rx.merge
        var sourceStream$ = collectionName === 'onlineOffline' // include websocket subscription stream
            ? Rx.merge(Rx.combineLatest([
                this.webSocketService.subscribeOnlineOffline(requestStream$.pipe(map(function (_a) {
                    var _b = _a.ref.path, collection = _b.collection, authUid = _b.authUid, ids = _b.ids;
                    return ids;
                }))),
                requestStream$,
            ]).pipe(map(function (_a) {
                var ss = _a[0], ref = _a[1];
                return ref;
            })), requestStream$)
            : requestStream$;
        return sourceStream$.pipe(switchMap(function (_a) {
            var _b = _a.ref.path, collection = _b.collection, authUid = _b.authUid, ids = _b.ids;
            return ids.length === 0
                ? Rx.of([])
                : _this.store$.pipe(tap(function (store) { return _this.verifyModuleIsImported(collection, store); }), // TODO: T75102
                select(collection, authUid), filter(isDefined), map(function (entitiesObject) {
                    return objectFilterByKeys(entitiesObject.entities, ids);
                }), map(function (entitiesObject) {
                    return Object.keys(entitiesObject).length
                        ? Object.values(entitiesObject)
                        : undefined;
                }), filter(isDefined), map(function (entities) { return entities.map(function (entity) { return entity.entity; }); }), distinctUntilChanged(sameElements));
        }), publishReplay(1), refCount());
    };
    // Hits an elastic search endpoint directly and returns the result.
    // The data doesn't get stored in the datastore (at least yet).
    Datastore.prototype.search = function (key, query$) {
        var _this = this;
        return Rx.combineLatest([
            this.storeBackend.search(key, toObservable(query$)),
            this.auth.authState$,
        ]).pipe(map(function (_a) {
            var result = _a[0], authState = _a[1];
            return _this.searchManager.transform(key, result, authState ? authState.userId : '0');
        }), publishReplay(1), refCount());
    };
    /* Creates a single object.
     */
    Datastore.prototype.createObject = function (collectionName, object, extra) {
        var _this = this;
        return Rx.combineLatest([
            toObservable(collectionName),
            this.auth.authState$,
        ])
            .pipe(map(function (_a) {
            var collection = _a[0], authState = _a[1];
            var path = {
                collection: collection,
                authUid: authState ? authState.userId : LOGGED_OUT_KEY,
            };
            return { path: path };
        }), take(1), switchMap(function (ref) { return _this.storeBackend.push(ref, object, extra); }))
            .toPromise();
    };
    Datastore.prototype.generateClientRequestId = function () {
        return Math.random()
            .toString(36)
            .substr(2, 16);
    };
    Datastore.prototype.verifyModuleIsImported = function (collection, state) {
        if (!(collection in state)) {
            throw new DatastoreMissingModuleError(collection);
        }
    };
    return Datastore;
}());
export { Datastore };
